import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ChannelSheetService } from '@service/channel/channel-sheet.service';
import { ChannelGroup, MAX_CHANNEL_GROUPS } from 'icreate/common/models/channel-group';
import { DialogType } from '../dialog-channel-category/dialog-channel-category.component';
import { ChannelGroupService } from '@service/channel/channel-group.service';
import { Subject, takeUntil, lastValueFrom, take } from 'rxjs';
import { DialogChannelGroupComponent } from '../dialog-channel-group/dialog-channel-group.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ChannelService } from '@service/channel/channel.service';
import { DeleteConfirmationDialogComponent } from '@components/delete-confirmation-dialog/delete-confirmation-dialog.component';
import _ from 'lodash';
import { VesselService } from '@service/vessel.service';
import { systemChannelGroupNum } from 'icreate/common/models/system-channels';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { MoreOptionsComponent } from '../more-options/more-options.component';

@Component({
    selector: 'app-channel-panel-group',
    templateUrl: './channel-panel-group.component.html',
    styleUrls: ['./channel-panel-group.component.scss'],
    standalone: false,
})
export class ChannelPanelGroupComponent implements OnInit, OnDestroy {
    @Input() selectedChannel: any;
    @Input() selectedChannelId: string;
    @Input() canEditSheet: boolean;

    channelGroupList: ChannelGroup[] = [];
    activeGroup: ChannelGroup;
    selectedChannelGroup: ChannelGroup = new ChannelGroup();
    isChannelUpdated: boolean = false;

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        private channelSheetService: ChannelSheetService,
        private channelGroupService: ChannelGroupService,
        private translateService: TranslateService,
        private channelService: ChannelService,
        private vesselService: VesselService,
        private dialog: MatDialog,
        private overlay: Overlay
    ) {}

    overlayRef: OverlayRef;

    ngOnInit() {
        this.channelGroupService.list$.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(channelGroupList => {
            this.channelGroupList = channelGroupList;
        });

        this.channelSheetService.isChannelUpdated$
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(hasUnsavedChanges => {
                this.isChannelUpdated = hasUnsavedChanges;
            });

        this.channelSheetService.activeGroup$
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(activeGroup => {
                this.activeGroup = activeGroup;
            });
    }

    ngOnDestroy(): void {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    async selectGroup(group: ChannelGroup) {
        if (this.isChannelUpdated) {
            const dialogRef = this.dialog.open(DialogChannelGroupComponent, {
                width: '474px',
                data: {
                    type: DialogType.description,
                    title: '',
                    description: 'channel.group.change-select',
                },
            });
            const shouldSetSelectedChannel = await lastValueFrom(dialogRef.afterClosed());
            if (shouldSetSelectedChannel) {
                this.channelSheetService.setActiveGroup(group);
                this.channelSheetService.setIsChannelUpdated(false);
            }
        } else {
            if (this.activeGroup.id !== group.id) {
                this.channelSheetService.setActiveGroup(group);
            }
        }
    }

    openMore(event: MouseEvent, group: ChannelGroup) {
        event.stopImmediatePropagation();

        this.selectedChannelGroup = group;

        this.overlayRef?.dispose();

        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(event.target as HTMLElement)
            .withPositions([
                {
                    originX: 'end',
                    originY: 'bottom',
                    overlayX: 'end',
                    overlayY: 'top',
                },
            ]);

        this.overlayRef = this.overlay.create({
            positionStrategy,
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-transparent-backdrop',
        });

        const portal = new ComponentPortal(MoreOptionsComponent);
        const componentRef = this.overlayRef.attach(portal);
        componentRef.instance.disableOptions = !this.canEditSheet;
        componentRef.instance.disableDelete = this.isSystemChannelGroup();

        //plus 1 for system channel group
        componentRef.instance.disableDuplicate =
            this.channelGroupList.length > MAX_CHANNEL_GROUPS || this.isSystemChannelGroup();

        componentRef.instance.onDeleteEmitter.subscribe(() => this.onDelete());
        componentRef.instance.onEditEmitter.subscribe(() => this.onEdit());
        componentRef.instance.onDuplicateEmitter.subscribe(() => this.onDuplicate());

        this.overlayRef
            .backdropClick()
            .pipe(take(1))
            .subscribe(() => this.closeMore());
    }

    async onEdit() {
        const dialogRef = this.dialog.open(DialogChannelGroupComponent, {
            width: '474px',
            data: {
                title: 'channel.edit-group.title',
                type: DialogType.edit,
                selectedChannelGroup: this.selectedChannelGroup,
            },
        });

        const groupToEdit: ChannelGroup = await lastValueFrom(dialogRef.afterClosed());
        if (groupToEdit) {
            try {
                await this.channelGroupService.update(groupToEdit, this.vesselService.getActiveVesselId());
            } catch (err) {
                console.error(err);
            } finally {
                this.channelSheetService.hideLoading();
            }
        }

        this.closeMore();
    }

    async onDuplicate() {
        if (this.isSystemChannelGroup()) return;
        const copiedGroup = { ...this.selectedChannelGroup };
        copiedGroup.id = '';
        const newGroupName = this.getDuplicateGroupName(copiedGroup.displayName, this.channelGroupList);
        copiedGroup.displayName = newGroupName;

        const dialogRef = this.dialog.open(DialogChannelGroupComponent, {
            width: '474px',
            data: {
                title: 'channel.add-group',
                type: DialogType.add,
                selectedChannelGroup: copiedGroup,
                isDuplicate: true,
            },
        });

        const groupToAdd: ChannelGroup = await lastValueFrom(dialogRef.afterClosed());
        try {
            this.channelSheetService.showLoading();
            const newGroup = await this.channelGroupService.add(groupToAdd, groupToAdd.vesselId);
            const channelList = this.channelSheetService.initChannelRows(newGroup);
            await this.channelService.create(channelList);
        } catch (err) {
            console.error(err);
        } finally {
            this.closeMore();
            this.channelSheetService.hideLoading();
        }
        return;
    }

    async onDelete() {
        if (this.isSystemChannelGroup()) return;
        const dialogRef = this.dialog.open(DialogChannelGroupComponent, {
            width: '474px',
            data: {
                title: this.getTranslate('channel.delete-group.title'),
                description: this.getTranslate('channel.delete-group.content'),
                type: DialogType.delete,
                selectedChannelGroup: this.selectedChannelGroup,
            },
        });

        const groupToDelete: ChannelGroup = await lastValueFrom(dialogRef.afterClosed());
        if (!groupToDelete) {
            this.closeMore();
            return;
        }

        const advancedDialogRef = this.dialog.open(DeleteConfirmationDialogComponent, {
            width: '474px',
            data: {
                title: 'channel.more-option.groups.delete.title',
                name: this.selectedChannelGroup.displayName,
                message1: 'channel.more-option.groups.delete.message1',
                message2: 'channel.more-option.groups.delete.message2',
                cancelButton: 'channel.more-option.groups.delete.button.cancel',
                buttonDelete: 'channel.more-option.groups.delete.button.delete',
                inputLegend: 'channel.more-option.groups.delete.button.legend',
            },
        });

        const advancedConfirmed = await lastValueFrom(advancedDialogRef.afterClosed());
        if (!advancedConfirmed) {
            this.closeMore();
            return;
        }

        try {
            await this.channelGroupService.delete(groupToDelete.id, this.vesselService.getActiveVesselId());
            if (this.channelGroupList.length) {
                this.channelSheetService.setActiveGroup(this.channelGroupList[0]);
            } else {
                this.channelSheetService.setActiveGroup(null);
            }
        } catch (err) {
            console.error(err);
        } finally {
            this.channelSheetService.hideLoading();
        }
        this.closeMore();
    }

    isSystemChannelGroup(): boolean {
        if (!this.selectedChannelGroup) return false;
        return this.selectedChannelGroup.rowNum == systemChannelGroupNum;
    }

    getTranslate(text) {
        return this.translateService.instant(text, {
            group: this.selectedChannelGroup.displayName,
        });
    }

    closeMore = () => {
        this.overlayRef?.dispose();
        this.overlayRef = null;
    };

    getDuplicateGroupName(groupName: string, groupList: ChannelGroup[]) {
        let uniqueName = groupName;
        let counter = 1;

        while (groupList.some(group => group.displayName === uniqueName)) {
            uniqueName = `${groupName}_copy_${counter}`;
            counter++;
        }

        return uniqueName;
    }
}
